/**
 * Utility functions for safely handling redirect URLs
 */

interface SafeRedirectOptions {
  url: string | null | undefined;
  defaultPath?: string;
  allowSubpaths?: boolean;
  allowedPaths?: string[];
}

/**
 * Validates if a URL is a safe relative URL that can be used for redirects
 * Prevents open redirect vulnerabilities by ensuring URLs are relative paths
 * 
 * @param options Configuration options for the safe redirect
 * @returns A safe relative URL
 */
export function getSafeRedirectUrl(options: SafeRedirectOptions): string {
  const { 
    url, 
    defaultPath = '/',
    allowSubpaths = true,
    allowedPaths = []
  } = options;
  
  // If no URL is provided, return the default path
  if (!url) {
    return defaultPath;
  }
  
  // Check if the URL is a safe relative path:
  // - Must start with /
  // - Must not start with // (protocol-relative URL)
  // - Must not contain : (protocol separator)
  // - Must not contain \ (Windows path separator)
  // - Must not contain encoded versions of these characters
  const isSafe = url.startsWith('/') && 
                !url.startsWith('//') && 
                !url.includes(':') &&
                !url.includes('\\') &&
                !url.includes('%2F%2F') &&
                !url.includes('%3A') &&
                !url.includes('%5C') &&
                !url.includes('%252F%252F') &&
                !url.includes('%253A') &&
                !url.includes('%255C');
  
  if (!isSafe) {
    return defaultPath;
  }
  
  // If we have a list of allowed paths, check if the URL starts with one of them
  if (allowedPaths.length > 0) {
    const isAllowedPath = allowedPaths.some(path => {
      if (allowSubpaths) {
        return url === path || url.startsWith(`${path}/`);
      }
      return url === path;
    });
    
    if (!isAllowedPath) {
      return defaultPath;
    }
  }
  
  return url;
}

interface AppendRedirectOptions {
  baseUrl: string;
  redirectUrl: string | null | undefined;
  defaultPath?: string;
  paramName?: string;
  allowSubpaths?: boolean;
  allowedPaths?: string[];
}

/**
 * Appends a redirect parameter to a URL
 * 
 * @param options Configuration options for appending the redirect parameter
 * @returns The base URL with the redirect parameter appended
 */
export function appendRedirectParam(options: AppendRedirectOptions): string {
  const { 
    baseUrl, 
    redirectUrl,
    defaultPath = '/',
    paramName = 'redirect',
    allowSubpaths = true,
    allowedPaths = []
  } = options;
  
  const safeRedirect = getSafeRedirectUrl({
    url: redirectUrl,
    defaultPath,
    allowSubpaths,
    allowedPaths
  });
  
  // If the redirect is the default path, don't append it
  if (safeRedirect === defaultPath) {
    return baseUrl;
  }
  
  // Check if the base URL already has query parameters
  const separator = baseUrl.includes('?') ? '&' : '?';
  
  return `${baseUrl}${separator}${paramName}=${encodeURIComponent(safeRedirect)}`;
} 